import { DOT, EMPTY, MAX_SIZE, SHADED, type CellState } from "./types";
import type { LitsDefinition, LitsProgress, LitsSubmission } from "./types";

const DEFAULT_RULES = {
  en: "Shade a tetromino (four orthogonally connected cells) in every region. All shaded cells form one connected group, no 2×2 block is fully shaded, and two congruent tetrominoes (rotations and reflections count as the same) never share an edge.",
  fr: "Noircissez un tétromino (quatre cases reliées par leurs côtés) dans chaque région. Toutes les cases noircies forment un seul groupe, aucun carré 2×2 n'est entièrement noirci, et deux tétrominos de même forme (rotations et symétries comprises) ne se touchent jamais par un côté.",
};

export function emptyDefinition(): LitsDefinition {
  return {
    puzzleType: "lits",
    typeVersion: 1,
    rows: 6,
    cols: 6,
    regions: Array.from({ length: 6 }, () => "A".repeat(6)),
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SIZE, Math.max(1, Math.trunc(value)));
}

export function blankRows(rows: number, cols: number, fill: CellState = EMPTY): string[] {
  return Array.from({ length: rows }, () => fill.repeat(cols));
}

/** Crops or pads a grid to the given size, keeping the top-left corner. */
export function resizeRows(source: string[], rows: number, cols: number, pad = EMPTY): string[] {
  return Array.from({ length: rows }, (_, r) => {
    const row = source[r] ?? "";
    return row.length >= cols ? row.slice(0, cols) : row + pad.repeat(cols - row.length);
  });
}

function sanitizeRow(raw: unknown, allowed: readonly string[]): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw, (char) => (allowed.includes(char) ? char : EMPTY)).join("");
}

export function emptyProgress(definition: LitsDefinition): LitsProgress {
  return { version: 1, rows: blankRows(definition.rows, definition.cols) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: LitsDefinition, raw: unknown): LitsProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = (raw as { rows?: unknown }).rows;
  if (!Array.isArray(source)) return emptyProgress(definition);
  const rows = source.map((row) => sanitizeRow(row, [SHADED, EMPTY, DOT]));
  return { version: 1, rows: resizeRows(rows, definition.rows, definition.cols) };
}

/** The server only reads shaded and unshaded: dots are the player's own notes. */
export function serializeSubmission(
  definition: LitsDefinition,
  progress: LitsProgress,
): LitsSubmission {
  return {
    rows: resizeRows(progress.rows, definition.rows, definition.cols).map((row) =>
      row.replaceAll(DOT, EMPTY),
    ),
  };
}
